import { useState, useRef, useEffect, memo } from "react";
import {
  TableRow,
  TableCell,
  Stack,
  Box,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputBase,
  Button,
  Tooltip,
  Menu,
  Link,
  alpha,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BlockIcon from "@mui/icons-material/Block";
import StarIcon from "@mui/icons-material/Star";
import HistoryIcon from "@mui/icons-material/History";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BusinessIcon from "@mui/icons-material/Business";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import { InteractionOrder, Interaction, InteractionType } from "../types/interactions";
import TBULogo from "../../businessInsights/TBULogo";
import { SKIP_REASONS, PRE_INTERACTION_INSTRUCTIONS } from "../constants";
import { fCurrency } from "../../../utils/formatNumber";
import { formatDate } from "../../../../utils";
import { formatPhoneNumber } from "../utils/formatPhone";
import { PastInteractionsModal } from "./PastInteractionsModal";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

interface InteractionRowProps {
  order: InteractionOrder;
  interaction: Interaction | undefined;
  statusFilter: string;
  useClosedTabOrderLayout?: boolean;
  interactionTypes: InteractionType[];
  onInteractionTypeChange: (
    orderNumber: string,
    type: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  onInteractionNoteChange: (
    orderNumber: string,
    note: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  onPreInstructionsChange: (
    orderNumber: string,
    instructions: string,
    contactId: number,
    settlementDate?: string | null
  ) => void;
  onSkipWithReason: (orderNumber: string, reason: string, contactId: number, settlementDate?: string | null) => void;
  onUndoInteraction: (orderNumber: string, contactId: number) => void;
  onClosedConfirmationChange: (orderNumber: string, isConfirmed: boolean) => void;
  getStatusColor: (status: string) => string;
  isTaggedSection?: boolean;
  onTagOrderContacts?: (orderNumber: string) => void;
  onUntagContact?: (orderNumber: string, contactId: number) => void;
}

export const InteractionRow = memo(({
  order,
  interaction,
  statusFilter,
  useClosedTabOrderLayout = false,
  interactionTypes,
  onInteractionTypeChange,
  onInteractionNoteChange,
  onPreInstructionsChange,
  onSkipWithReason,
  onUndoInteraction,
  onClosedConfirmationChange,
  getStatusColor,
  isTaggedSection = false,
  onTagOrderContacts,
  onUntagContact
}: InteractionRowProps) => {
  const theme = useTheme();
  const [skipMenuAnchor, setSkipMenuAnchor] = useState<HTMLElement | null>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [isPastInteractionsModalOpen, setIsPastInteractionsModalOpen] = useState(false);
  const [localPreInstructions, setLocalPreInstructions] = useState(order.preInteractionInstructions || "");
  const preInstructionsRef = useRef(localPreInstructions);

  // Sync if the parent resets the value (e.g. date change reloads orders)
  useEffect(() => {
    const incoming = order.preInteractionInstructions || "";
    if (incoming !== preInstructionsRef.current) {
      setLocalPreInstructions(incoming);
      preInstructionsRef.current = incoming;
    }
  }, [order.preInteractionInstructions]);

  const [localNote, setLocalNote] = useState(interaction?.note || "");
  const noteRef = useRef(localNote);

  // Sync if the parent resets the note (e.g. undo, date change)
  useEffect(() => {
    const incoming = interaction?.note || "";
    if (incoming !== noteRef.current) {
      setLocalNote(incoming);
      noteRef.current = incoming;
    }
  }, [interaction?.note]);

  const orderNumber = order.orderNumber || "";
  const contactId = order.phonebookContactId || 0;
  const hasInteraction = interaction?.type;
  const isSkipped = interaction?.skipped;
  const hasAnyInteraction = hasInteraction || isSkipped;
  const isTaggedForFollowUp = Boolean(order.isTaggedForFollowUp);

  const propertyAddress = order.allPropertiesInFile || "";

  const handleOpenSkipMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSkipMenuAnchor(event.currentTarget);
  };

  const handleCloseSkipMenu = () => {
    setSkipMenuAnchor(null);
  };

  const handleSkipWithReason = (reason: string) => {
    onSkipWithReason(orderNumber, reason, contactId, order.settlementDate);
    handleCloseSkipMenu();
  };

  const handleOpenTypeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setTypeMenuAnchor(event.currentTarget);
  };

  const handleCloseTypeMenu = () => {
    setTypeMenuAnchor(null);
  };

  const handleSelectType = (typeId: number) => {
    onInteractionTypeChange(
      orderNumber,
      typeId.toString(),
      contactId,
      order.preInteractionInstructions || undefined,
      order.settlementDate
    );
    handleCloseTypeMenu();
  };

  const handleTagClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!orderNumber) return;

    if (isTaggedForFollowUp) {
      onUntagContact?.(orderNumber, contactId);
    } else {
      onTagOrderContacts?.(orderNumber);
    }
  };

  const getRowBgColor = () => {
    if (isTaggedSection) return alpha(theme.palette.primary.main, 0.08);
    if (hasInteraction) return alpha(theme.palette.success.main, 0.03);
    if (isSkipped) return alpha(theme.palette.warning.main, 0.03);
    if (isTaggedForFollowUp) return alpha(theme.palette.primary.main, 0.04);
    return "transparent";
  };

  const getRowHoverColor = () => {
    if (isTaggedSection) return alpha(theme.palette.primary.main, 0.12);
    if (hasInteraction) return alpha(theme.palette.success.main, 0.06);
    if (isSkipped) return alpha(theme.palette.warning.main, 0.06);
    if (isTaggedForFollowUp) return alpha(theme.palette.primary.main, 0.08);
    return alpha(theme.palette.primary.main, 0.02);
  };

  const rowAccentColor = hasInteraction
    ? theme.palette.success.main
    : isSkipped
    ? theme.palette.warning.main
    : isTaggedForFollowUp
    ? theme.palette.primary.main
    : null;
  const tagTooltip = order.taggedByUser
    ? `Flagged by ${order.taggedByUser.firstName} ${order.taggedByUser.lastName}${
        order.taggedAt ? ` on ${formatDate(order.taggedAt)}` : ""
      }`
    : "Flagged for follow-up";
  const orderDate =
    statusFilter === "Closed"
      ? order.settlementDate
        ? formatDate(order.settlementDate)
        : ""
      : order.receivedDate
      ? formatDate(order.receivedDate)
      : "";

  return (
    <>
      <TableRow
        sx={{
          transition: "all 0.15s ease",
          bgcolor: getRowBgColor(),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          boxShadow: rowAccentColor ? `inset 4px 0 0 ${rowAccentColor}` : "none",
          "&:hover": {
            bgcolor: getRowHoverColor(),
            boxShadow: rowAccentColor
              ? `inset 4px 0 0 ${rowAccentColor}`
              : `inset 3px 0 0 ${theme.palette.primary.main}`
          },
          "&:last-child": {
            borderBottom: "none"
          },
          "& td": {
            py: 1.25
          }
        }}
      >
        <TableCell>
          <Stack direction="row" spacing={1} alignItems={useClosedTabOrderLayout ? "flex-start" : "center"}>
            <Box sx={{ minWidth: 0 }}>
              <Box display="flex" alignItems={useClosedTabOrderLayout ? "flex-start" : "center"} gap={0.5}>
                {useClosedTabOrderLayout ? (
                  <Box
                    sx={{
                      width: 24,
                      minWidth: 24,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    {statusFilter === "Closed" && onTagOrderContacts && onUntagContact && (
                      <Tooltip
                        title={
                          isTaggedForFollowUp
                            ? `${tagTooltip}. Click to remove follow-up tag.`
                            : "Tag this order for follow-up"
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!orderNumber}
                            onClick={handleTagClick}
                            sx={{
                              p: 0.25,
                              color: isTaggedForFollowUp ? theme.palette.primary.main : theme.palette.text.disabled,
                              flexShrink: 0,
                              "&:hover": {
                                color: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.1)
                              }
                            }}
                          >
                            {isTaggedForFollowUp ? (
                              <FlagIcon sx={{ fontSize: "1rem" }} />
                            ) : (
                              <OutlinedFlagIcon sx={{ fontSize: "1rem" }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                ) : null}
                <Box sx={{ minWidth: 0 }}>
                  <Link href={`${momentumUrl}new-home/${orderNumber}`} target="_blank" underline="hover">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        whiteSpace: "nowrap"
                      }}
                    >
                      {orderNumber}
                    </Typography>
                  </Link>
                  {useClosedTabOrderLayout ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {orderDate}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              {!useClosedTabOrderLayout ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {orderDate}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {order.transactionType === "Purchase" && <Chip label="P" color="primary" size="small" />}
            {order.transactionType === "Refinance" && <Chip label="R" color="info" size="small" />}
          </Typography>
        </TableCell>

        <TableCell>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip
                title={
                  order.preferredName ? `${order.contactName || ""} (${order.preferredName})` : order.contactName || ""
                }
                arrow
              >
                <Link
                  href={`/phonebook/contact/profile?id=${contactId}`}
                  target="_blank"
                  underline="hover"
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {order.contactName || ""}
                    {order.preferredName && (
                      <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                        {" "}
                        ({order.preferredName})
                      </Typography>
                    )}
                  </Typography>
                </Link>
              </Tooltip>
              <Box display="flex" alignItems="center" gap={0.5}>
                {order.isClient && (
                  <StarIcon
                    sx={{
                      fontSize: "1rem",
                      color: theme.palette.warning.main,
                      flexShrink: 0
                    }}
                  />
                )}
                {order.isDealOwner && (
                  <AttachMoneyIcon
                    sx={{
                      fontSize: "1rem",
                      color: theme.palette.text.secondary,
                      flexShrink: 0
                    }}
                  />
                )}
                {order.isTbuContact === "Yes" && (
                  <Tooltip title="TBU Contact" arrow>
                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 0.25 }}>
                      <TBULogo height={14} color={theme.palette.text.primary} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              {order.pastInteractionsCount > 0 && (
                <Tooltip
                  title={`${order.pastInteractionsCount} past interaction${order.pastInteractionsCount > 1 ? "s" : ""}`}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPastInteractionsModalOpen(true);
                    }}
                    sx={{
                      p: 0.25,
                      color: theme.palette.primary.main,
                      flexShrink: 0,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {order.companyName && (
              <Tooltip title={order.companyName} arrow>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {order.companyName}
                </Typography>
              </Tooltip>
            )}
            {order.contactPosition && (
              <Tooltip title={order.contactPosition} arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                  sx={{ fontStyle: "italic" }}
                >
                  {order.contactPosition}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </TableCell>

        <TableCell>
          <Tooltip title={propertyAddress} arrow>
            <Typography variant="body2" color="text.secondary" noWrap>
              {propertyAddress}
            </Typography>
          </Tooltip>
        </TableCell>

        <TableCell>
          <Stack spacing={0.25}>
            <Tooltip title={order.businessEmail || ""} arrow>
              <Typography
                component="a"
                href={`mailto:${order.businessEmail || ""}`}
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                }}
                noWrap
              >
                {order.businessEmail || ""}
              </Typography>
            </Tooltip>
            {order.mobilePhone && (
              <Tooltip title="Mobile" arrow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIphoneIcon sx={{ fontSize: "0.875rem", color: "text.secondary" }} />
                  <Typography
                    component="a"
                    href={`tel:${order.mobilePhone}`}
                    variant="body2"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {formatPhoneNumber(order.mobilePhone)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {order.officePhone && (
              <Tooltip title="Office" arrow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: "0.875rem", color: "text.secondary" }} />
                  <Typography
                    component="a"
                    href={`tel:${order.officePhone}`}
                    variant="body2"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {formatPhoneNumber(order.officePhone)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        <TableCell>
          <Chip
            label={order.customOrderStatus || ""}
            size="small"
            sx={{
              height: "auto",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: alpha(getStatusColor(order.customOrderStatus || ""), 0.1),
              color: getStatusColor(order.customOrderStatus || ""),
              border: `1px solid ${alpha(getStatusColor(order.customOrderStatus || ""), 0.2)}`,
              maxWidth: "none",
              "& .MuiChip-label": {
                whiteSpace: "nowrap",
                overflow: "visible"
              }
            }}
          />
        </TableCell>

        {statusFilter === "Closed" && (
          <TableCell sx={{ py: 1 }} align="center">
            <Tooltip
              title={
                order.isClosedConfirmed && order.closedConfirmedByUser
                  ? `Confirmed by ${order.closedConfirmedByUser.firstName} ${
                      order.closedConfirmedByUser.lastName
                    }${order.closedConfirmedAt ? ` on ${formatDate(order.closedConfirmedAt)}` : ""}`
                  : ""
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={() => onClosedConfirmationChange(orderNumber, !order.isClosedConfirmed)}
                sx={{
                  color: order.isClosedConfirmed ? theme.palette.success.main : theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.success.main,
                    bgcolor: alpha(theme.palette.success.main, 0.1)
                  }
                }}
              >
                {order.isClosedConfirmed ? (
                  <TaskAltIcon sx={{ fontSize: "1.25rem" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: "1.25rem" }} />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
        )}

        <TableCell align="right">
          <Typography variant="body2" fontWeight={700} color="primary">
            {order.salesLoan ? fCurrency(order.salesLoan).replace(".00", "") : null}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            py: 1,
            borderRight: `2px solid ${theme.palette.divider}`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
              <InputBase
                value={localPreInstructions}
                placeholder="Add pre-interaction instructions..."
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                sx={{
                  fontSize: "0.813rem",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "6px",
                  border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    bgcolor: theme.palette.background.paper
                  },
                  "&:focus-within": {
                    borderColor: theme.palette.primary.main,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setLocalPreInstructions(value);
                  preInstructionsRef.current = value;
                  onPreInstructionsChange(orderNumber, value, contactId, order.settlementDate);
                }}
              />
              {order.preInteractionInstructionsBy && order.preInteractionInstructionsAt && (
                <Typography
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    fontSize: "0.65rem",
                    lineHeight: 1.4,
                    color: "text.disabled",
                    px: 1.25,
                    pt: 0.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%"
                  }}
                >
                  {order.preInteractionInstructionsBy.firstName} {order.preInteractionInstructionsBy.lastName} · {formatDate(order.preInteractionInstructionsAt)}
                </Typography>
              )}
            </Box>
            {!hasAnyInteraction && (
              <Box sx={{ minWidth: 75, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenSkipMenu}
                  startIcon={<BlockIcon sx={{ fontSize: "0.9rem" }} />}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    py: 0.5,
                    px: 1.5,
                    borderRadius: "6px",
                    textTransform: "none",
                    borderColor: alpha(theme.palette.warning.main, 0.3),
                    color: "warning.dark",
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                    "&:hover": {
                      borderColor: theme.palette.warning.main,
                      bgcolor: alpha(theme.palette.warning.main, 0.1)
                    }
                  }}
                >
                  Skip
                </Button>
                <Menu
                  anchorEl={skipMenuAnchor}
                  open={Boolean(skipMenuAnchor)}
                  onClose={handleCloseSkipMenu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center"
                  }}
                >
                  {SKIP_REASONS.map((reason) => (
                    <MenuItem
                      key={reason}
                      onClick={() => handleSkipWithReason(reason)}
                      sx={{
                        fontSize: "0.813rem",
                        py: 1
                      }}
                    >
                      {reason}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Stack>
        </TableCell>

        <TableCell sx={{ py: 1 }}>
          {isSkipped ? (
            <Chip
              label={interaction?.skipReason || "Skipped"}
              size="small"
              icon={<BlockIcon sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }} />}
              onDelete={() => onUndoInteraction(orderNumber, contactId)}
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.65rem", md: "0.72rem" },
                height: "auto",
                minHeight: { xs: 24, md: 28 },
                maxWidth: { xs: "100%", md: "none" },
                borderRadius: "6px",
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.dark,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                "& .MuiChip-label": {
                  whiteSpace: { xs: "nowrap", md: "normal" },
                  overflow: { xs: "hidden", md: "visible" },
                  textOverflow: { xs: "ellipsis", md: "clip" },
                  px: { xs: 0.5, md: 1 },
                  py: 0.5
                },
                "& .MuiChip-icon": {
                  ml: { xs: 0.5, md: 1 }
                },
                "& .MuiChip-deleteIcon": {
                  color: theme.palette.warning.dark,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  mr: { xs: 0.25, md: 0.5 },
                  "&:hover": {
                    color: theme.palette.error.main
                  }
                }
              }}
            />
          ) : (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ minWidth: { xs: 90, md: 140 }, maxWidth: { xs: 120, md: "none" }, flexShrink: 0 }}>
                <Chip
                  label={
                    interaction?.type
                      ? interactionTypes.find((t) => t.id.toString() === interaction.type)?.name || "Select type..."
                      : "Select type..."
                  }
                  size="small"
                  onClick={handleOpenTypeMenu}
                  onDelete={interaction?.type ? () => onUndoInteraction(orderNumber, contactId) : undefined}
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", md: "0.72rem" },
                    width: "100%",
                    height: "auto",
                    minHeight: { xs: 24, md: 28 },
                    borderRadius: "6px",
                    bgcolor: interaction?.type
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.grey[500], 0.08),
                    color: interaction?.type ? theme.palette.primary.dark : theme.palette.text.secondary,
                    border: `1px solid ${
                      interaction?.type ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.grey[500], 0.2)
                    }`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: interaction?.type
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha(theme.palette.grey[500], 0.12),
                      borderColor: interaction?.type
                        ? alpha(theme.palette.primary.main, 0.5)
                        : alpha(theme.palette.grey[500], 0.3)
                    },
                    "& .MuiChip-label": {
                      whiteSpace: { xs: "nowrap", md: "normal" },
                      overflow: { xs: "hidden", md: "visible" },
                      textOverflow: { xs: "ellipsis", md: "clip" },
                      px: { xs: 0.75, md: 1.5 },
                      py: 0.5
                    },
                    "& .MuiChip-deleteIcon": {
                      color: interaction?.type ? theme.palette.primary.dark : "transparent",
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      mr: { xs: 0.25, md: 0.5 },
                      "&:hover": {
                        color: theme.palette.error.main
                      }
                    }
                  }}
                />
                <Menu
                  anchorEl={typeMenuAnchor}
                  open={Boolean(typeMenuAnchor)}
                  onClose={handleCloseTypeMenu}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.5,
                        minWidth: 160,
                        boxShadow: theme.shadows[8]
                      }
                    }
                  }}
                >
                  {interactionTypes
                    .filter((type) => type.name !== "Skipped")
                    .map((type) => (
                      <MenuItem
                        key={type.id}
                        onClick={() => handleSelectType(type.id)}
                        selected={interaction?.type === type.id.toString()}
                        sx={{
                          fontSize: "0.813rem",
                          py: 1
                        }}
                      >
                        {type.name}
                      </MenuItem>
                    ))}
                </Menu>
              </Box>
              <InputBase
                value={localNote}
                placeholder="Add interaction notes..."
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                sx={{
                  fontSize: "0.813rem",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "6px",
                  border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    bgcolor: theme.palette.background.paper
                  },
                  "&:focus-within": {
                    borderColor: theme.palette.primary.main,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setLocalNote(value);
                  noteRef.current = value;
                  onInteractionNoteChange(
                    orderNumber,
                    value,
                    contactId,
                    order.preInteractionInstructions || undefined,
                    order.settlementDate
                  );
                }}
              />
            </Stack>
          )}
        </TableCell>
      </TableRow>

      <PastInteractionsModal
        open={isPastInteractionsModalOpen}
        onClose={() => setIsPastInteractionsModalOpen(false)}
        contactId={contactId}
        orderNumber={orderNumber}
        contactName={order.contactName || ""}
        statusFilter={statusFilter}
      />
    </>
  );
});
